import http from "./httpService";
import userCartService from "./userCartService";

/**
 * @typedef {Object} MakercrateParams
 * @property {string} [search] - Fuzzy search by title, subheader, description, or hardware
 * @property {number} [page] - Page number
 * @property {number} [limit] - Number of records per page
 * @property {string} [sort_dir] - Sorting direction ('asc' | 'desc'), default: 'asc'
 * @property {string} [sort_field] - Field to sort by
 */

/**
 * @typedef {Object} MakercrateActivity
 * @property {string} id
 * @property {number} makercart_id
 * @property {string} makercart_activity
 * @property {string} activity_description
 * @property {string[]} image_thumbnail
 * @property {string[]} grade_level
 * @property {string} subject
 * @property {string} topics
 * @property {string} standards
 * @property {string[]} material_needed
 * @property {string} hardware_integration
 * @property {string[]} hardware_associated
 * @property {string[]} tags
 * @property {string} prototyping_material_integration
 * @property {string[]} focus
 * @property {string} maker_approach
 * @property {boolean} popular
 * @property {string} duration
 * @property {boolean} is_free
 * @property {boolean} public_visibility
 * @property {string} [learning_outcomes]
 */

/**
 * @typedef {Object} Makercrate
 * @property {number} id
 * @property {string} title
 * @property {number} price
 * @property {string} [hardware]
 * @property {string} subheader
 * @property {string} description
 * @property {MakercrateActivity[]} makercrate_activities
 */

/**
 * @typedef {Object} MakercrateListData
 * @property {number} page
 * @property {number} limit
 * @property {number} total
 * @property {number} totalPages
 * @property {Makercrate[]} rows
 */

class MakercrateService {
  /**
   * Get paginated list of makercrates
   * @param {MakercrateParams} param - Query parameters for filtering and pagination
   * @returns {Promise<{ success: boolean, message: string, data: MakercrateListData }>} Makercrate list response
   */
  async getMakercrates(param) {
    const url = http.getUrlWithParams("/makercrate-master", param);
    const response = await http.get(url);
    return response.data;
  }

  /**
   * Get a specific makercrate by ID
   * @param {number|string} id - Makercrate ID
   * @returns {Promise<{ success: boolean, message: string, data: Makercrate }>} Makercrate response
   */
  async getMakercrateById(id) {
    const response = await http.get(`/makercrate/${id}`);
    return response.data;
  }

  /**
   * Add a makercrate to the cart
   * @param {number|string} makercrateId - Makercrate ID
   * @param {string} [locale="US"] - Current cart locale
   * @returns {Promise<{ success: boolean, message: string, data?: unknown }>}
   */
  async addToCart(makercrateId, locale = "US") {
    // Read the latest quantity before deciding whether to create a bundle.
    const cartResponse = await userCartService.getUserCart(locale);
    if (
      cartResponse.success === false &&
      cartResponse.message !== "Cart not found"
    ) {
      return cartResponse;
    }

    const existingMakercrate = cartResponse.data?.makercrate?.find(
      (crate) => String(crate.makercrate_id) === String(makercrateId)
    );
    if (existingMakercrate) {
      const quantity = Number(existingMakercrate.quantity) || 1;
      return userCartService.updateBundleQuantity(
        existingMakercrate.id,
        quantity + 1
      );
    }

    const response = await http.post("/makercrate/add-to-cart", {
      makercrate_id: Number(makercrateId),
    });
    return response.data;
  }
}

export default new MakercrateService();
